import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import jsonData from "../../../../current-survey-numeric.json";
import "../../../../pages/D04_Responses_by_Demographic_Category.css";

const MultipleBarCharts = ({
  selectedQuestion,
  selectedDemographic,
  filterCriteria,
}) => {
  const [chartConfigs, setChartConfigs] = useState([]);
  const [demographicValues, setDemographicValues] = useState([]);

  useEffect(() => {
    const getSortedFieldValues = (fieldName, order = []) => {
      const values = [
        ...new Set(jsonData.map((item) => item[fieldName])),
      ].filter(Boolean);
      if (order.length > 0) {
        return values.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      }
      return values.sort((a, b) => a.localeCompare(b));
    };

    const demographicValues =
      selectedDemographic === "length_of_service_name"
        ? getSortedFieldValues("length_of_service_name", [
            "Less than 1 year",
            "1 to 2 years",
            "3 to 5 years",
            "6 to 10 years",
            "11 to 19 years",
            "20 years or more",
          ])
        : selectedDemographic === "generation_name"
        ? getSortedFieldValues("generation_name", [
            "Traditionalist",
            "Baby Boomer",
            "Generation X",
            "Millennial",
            "Generation Z",
            "Generation Next",
            "No Response",
          ])
        : getSortedFieldValues(selectedDemographic);

    setDemographicValues(demographicValues);

    if (!demographicValues || demographicValues.length === 0) {
      setChartConfigs([]);
      return;
    }

    // Step 1: Calculate total counts for each category
    const totalCategoryCounts = {
      favorable: 0,
      neutral: 0,
      unfavorable: 0,
      noResponse: 0,
    };

    jsonData.forEach((response) => {
      const answer = response[selectedQuestion];
      if (answer === 4 || answer === 5) {
        totalCategoryCounts.favorable++;
      } else if (answer === 3) {
        totalCategoryCounts.neutral++;
      } else if (answer === 1 || answer === 2) {
        totalCategoryCounts.unfavorable++;
      } else {
        totalCategoryCounts.noResponse++;
      }
    });

    // Step 2: Calculate percentages for each demographic group relative to the total for each category
    const favorabilityData = demographicValues.reduce(
      (acc, value) => {
        const filteredData = filterDataByCriteria(
          jsonData,
          selectedDemographic,
          value,
          filterCriteria
        );
        let favorable = 0;
        let neutral = 0;
        let unfavorable = 0;
        let noResponse = 0;

        filteredData.forEach((response) => {
          const answer = response[selectedQuestion];
          if (answer === 4 || answer === 5) {
            favorable++;
          } else if (answer === 3) {
            neutral++;
          } else if (answer === 1 || answer === 2) {
            unfavorable++;
          } else {
            noResponse++;
          }
        });

        // Calculate percentages against the total for each category
        acc.favorable.push((favorable / totalCategoryCounts.favorable) * 100);
        acc.neutral.push((neutral / totalCategoryCounts.neutral) * 100);
        acc.unfavorable.push(
          (unfavorable / totalCategoryCounts.unfavorable) * 100
        );
        acc.noResponse.push(
          (noResponse / totalCategoryCounts.noResponse) * 100
        );

        return acc;
      },
      { favorable: [], neutral: [], unfavorable: [], noResponse: [] }
    );

    // Step 3: Prepare chart configurations for each category
    const configs = [
      {
        id: "bars-NR",
        title: "No Response",
        series: [
          {
            name: "Percent",
            data: favorabilityData.noResponse,
          },
        ],
        colors: ["#bab0ac"],
      },
      {
        id: "bars-UF",
        title: "Unfavorable",
        series: [
          {
            name: "Percent",
            data: favorabilityData.unfavorable,
          },
        ],
        colors: ["#f28e2b"],
      },
      {
        id: "bars-NT",
        title: "Neutral",
        series: [
          {
            name: "Percent",
            data: favorabilityData.neutral,
          },
        ],
        colors: ["#f1ce63"],
      },
      {
        id: "bars-FV",
        title: "Favorable",
        series: [
          {
            name: "Percent",
            data: favorabilityData.favorable,
          },
        ],
        colors: ["#499894"],
      },
    ];

    setChartConfigs(configs);
  }, [selectedQuestion, selectedDemographic, filterCriteria]);

  useEffect(() => {
    const barHeight = 30;
    const chartHeight = demographicValues.length * barHeight + 100;

    const charts = chartConfigs.map((config) => {
      const element = document.querySelector(`#${config.id}`);
      if (element) {
        const chart = new ApexCharts(element, {
          series: config.series,
          colors: config.colors,
          chart: {
            type: "bar",
            height: chartHeight,
            width: "100%",
            stacked: false,
          },
          legend: {
            show: false,
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          title: {
            text: config.title,
            align: "center",
            style: {
              fontSize: "14px",
              fontFamily: "Ubuntu",
              color: "#3d3d3d",
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (val) => (isNaN(val) ? "0%" : `${val.toFixed(1)}%`),
          },
          stroke: {
            width: 1,
            colors: ["#fff"],
          },
          tooltip: {
            y: {
              formatter: (val) => (isNaN(val) ? "0%" : `${val.toFixed(1)}%`),
            },
          },
          xaxis: {
            categories: demographicValues,
          },
        });

        chart.render();
        return chart;
      }
      return null;
    });

    return () => {
      charts.forEach((chart) => {
        if (chart) chart.destroy();
      });
    };
  }, [chartConfigs, demographicValues]);

  const filterDataByCriteria = (
    data,
    demographicType,
    demographicValue,
    criteria
  ) => {
    return data.filter((item) => {
      if (!item[demographicType] || item[demographicType] === null)
        return false;
      let match = true;
      Object.keys(criteria).forEach((key) => {
        if (criteria[key].length > 0 && !criteria[key].includes(item[key])) {
          match = false;
        }
      });
      return match && item[demographicType] === demographicValue;
    });
  };

  return (
    <div className="D04_Responses_by_Demographic_Category-internal-charts">
      {chartConfigs.map((config) => (
        <div
          key={config.id}
          className="D04_Responses_by_Demographic_Category-internal-chart-container"
        >
          <div
            id={config.id}
            className="D04_Responses_by_Demographic_Category-internal-chart"
          ></div>
        </div>
      ))}
    </div>
  );
};

export default MultipleBarCharts;
